import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Grid,
} from '@mui/material';
import { createDonationCheckout } from '../../services/stripeService';

const PRESET_AMOUNTS = [200, 500, 1000, 2000, 3000, 5000] ; // Amounts in cents ($5, $10, $25, $50, $100)

export const DonationDialog = ({ open, handleClose }) => {
  const [amount, setAmount] = useState('');
  const [customAmount, setCustomAmount] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handlePresetClick = (presetAmount) => {
    setAmount(presetAmount);
    setCustomAmount('');
    setError(null);
  };

  const handleCustomAmountChange = (event) => {
    const value = event.target.value;
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      setCustomAmount(value);
      setAmount('');
      setError(null);
    }
  };

  const getAmountInCents = () => {
    if (customAmount) {
      const dollars = parseFloat(customAmount);
      if (isNaN(dollars) || dollars <= 0) {
        return null;
      }
      return Math.round(dollars * 100);
    }
    return amount || null;
  };

  const handleDonate = async () => {
    const amountInCents = getAmountInCents();
    
    if (!amountInCents) {
      setError('Please select or enter a valid donation amount');
      return;
    }

    if (amountInCents < 100) {
      setError('Minimum donation amount is $1.00');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const successUrl = `${window.location.origin}/?donation=success`;
      const cancelUrl = `${window.location.origin}/?donation=cancelled`;
      
      const checkoutUrl = await createDonationCheckout(amountInCents, successUrl, cancelUrl);
      
      // Redirect to Stripe Checkout
      window.location.href = checkoutUrl;
    } catch (err) {
      setError(err.message || 'Failed to process donation. Please try again.');
      setLoading(false);
    }
  };

  const handleCloseDialog = () => {
    if (!loading) {
      setAmount('');
      setCustomAmount('');
      setError(null);
      handleClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Typography variant="h5" component="div" align="center">
          Support NodeCanvas
        </Typography>
        <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 1 }}>
          Your donation helps us improve and maintain the platform
        </Typography>
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle1" gutterBottom>
            Select an amount:
          </Typography>
          
          <Grid container spacing={2} sx={{ mt: 1, mb: 3 }}>
            {PRESET_AMOUNTS.map((presetAmount) => (
              <Grid item xs={6} sm={4} key={presetAmount}>
                <Button
                  variant={amount === presetAmount ? 'contained' : 'outlined'}
                  fullWidth
                  onClick={() => handlePresetClick(presetAmount)}
                  disabled={loading}
                  sx={{
                    py: 1.5,
                    fontSize: '1rem',
                  }}
                >
                  ${(presetAmount / 100).toFixed(0)}
                </Button>
              </Grid>
            ))}
          </Grid>

          <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
            Or enter a custom amount:
          </Typography>
          
          <TextField
            fullWidth
            label="Custom Amount ($)"
            type="number"
            value={customAmount}
            onChange={handleCustomAmountChange}
            disabled={loading}
            placeholder="Enter amount"
            InputProps={{
              startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>,
            }}
            sx={{ mt: 1 }}
          />

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleCloseDialog} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleDonate}
          variant="contained"
          disabled={loading || (!amount && !customAmount)}
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          {loading ? 'Processing...' : 'Donate'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

